import { passwordStrength } from "@/lib/vaultCrypto";

/**
 * Passordhelse – analyse av dekrypterte oppføringer. Kjører KUN i nettleseren; ingenting sendes til server.
 *
 * Funn per oppføring:
 *  - leaked    : funnet i kjente datalekkasjer (HIBP)       alvorlighet kritisk
 *  - weak      : styrke 0–1 (veldig svakt/svakt)           alvorlighet høy
 *  - medium    : styrke 2 (middels)                       alvorlighet lav
 *  - reused    : samme passord brukt i ≥ 2 oppføringer   alvorlighet høy
 *  - old       : passord ikke byttet på > 365 dager       alvorlighet middels
 *  - missing   : mangler passord                           info
 */
export const OLD_DAYS = 365;

export const ISSUE_META = {
  leaked: { label: "Lekket", short: "Funnet i datalekkasje", severity: 4, description: "Passordet finnes i kjente datalekkasjer (Have I Been Pwned). Bytt det nå – og overalt du har brukt det samme." },
  weak: { label: "Svakt", short: "Svakt passord", severity: 3, description: "Lett å gjette eller knekke. Bytt til et langt, generert passord." },
  reused: { label: "Gjenbrukt", short: "Gjenbrukt passord", severity: 3, description: "Samme passord brukes flere steder. Lekker det ett sted, er alle stedene åpne." },
  old: { label: "Gammelt", short: "Over ett år gammelt", severity: 2, description: "Passordet er ikke byttet på over ett år." },
  medium: { label: "Middels", short: "Kunne vært sterkere", severity: 1, description: "Greit, men et lengre passord gir bedre margin." },
  missing: { label: "Mangler", short: "Mangler passord", severity: 1, description: "Oppføringen har ikke noe passord lagret." },
};

const daysSince = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null);

/**
 * @param {Array} entries dekrypterte oppføringer fra VaultContext
 * @param {Object} [breachResults] entry_id -> antall treff i lekkasjer (fra VaultContext.breach.results)
 */
export function analyzeVault(entries, breachResults = null) {
  const list = (entries || []).filter((e) => !e.corrupt);
  const leaks = breachResults || {};
  const counts = new Map();
  for (const e of list) {
    if (e.password) counts.set(e.password, (counts.get(e.password) || 0) + 1);
  }

  const items = list.map((e) => {
    const issues = [];
    let penalty = 0;
    if (!e.password) {
      issues.push("missing");
      penalty += 0.3;
    } else {
      if ((leaks[e.id] || 0) > 0) {
        issues.push("leaked");
        penalty += 0.8;
      }
      const st = passwordStrength(e.password);
      if (st.score <= 1) {
        issues.push("weak");
        penalty += 0.6;
      } else if (st.score === 2) {
        issues.push("medium");
        penalty += 0.2;
      }
      if ((counts.get(e.password) || 0) > 1) {
        issues.push("reused");
        penalty += 0.5;
      }
      const ref = e.password_changed_at || e.created_at;
      const age = daysSince(ref);
      if (age !== null && age > OLD_DAYS) {
        issues.push("old");
        penalty += 0.3;
      }
    }
    const priority = issues.reduce((sum, k) => sum + ISSUE_META[k].severity, 0);
    return { entry: e, issues, priority, penalty: Math.min(1, penalty), ageDays: daysSince(e.password_changed_at || e.created_at), strength: e.password ? passwordStrength(e.password) : null, breachCount: leaks[e.id] || 0 };
  });

  const flagged = items.filter((i) => i.issues.length).sort((a, b) => b.priority - a.priority || (a.entry.title || "").localeCompare(b.entry.title || "", "nb"));
  const count = (k) => items.filter((i) => i.issues.includes(k)).length;
  const total = items.length;
  const score = total ? Math.round((items.reduce((s, i) => s + (1 - i.penalty), 0) / total) * 100) : 100;
  const label = score >= 90 ? "Utmerket" : score >= 70 ? "Bra" : score >= 50 ? "Bør forbedres" : "Svak";
  const tone = score >= 90 ? "success" : score >= 70 ? "primary" : score >= 50 ? "warn" : "flag";

  return {
    total,
    score,
    label,
    tone,
    counts: { leaked: count("leaked"), weak: count("weak"), reused: count("reused"), old: count("old"), medium: count("medium"), missing: count("missing") },
    flagged,
    healthy: items.filter((i) => !i.issues.length).length,
    items,
  };
}
